/* eslint-disable camelcase */
import React, { useState } from "react"
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Tooltip,
  CircularProgress,
} from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import ImageIcon from "@mui/icons-material/Image"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import CloseIcon from "@mui/icons-material/Close"
import { useQuery, api } from "../../../../api"

// El componente recibe loccodigo y bodcodigo para el cálculo de stock
const DetalleArticulosGrid = ({ detalle, setFormData, tsolInEg, clicodigo, loccodigo, bodcodigo }) => {
  // Estado para manejar el visualizador (Modal) de Imágenes y PDFs
  const [mediaViewer, setMediaViewer] = useState({ open: false, type: "", title: "", items: [] })

  // Estado individual de carga por cada fila cuando consulta el stock/multimedia
  const [loadingRow, setLoadingRow] = useState(null)

  const filtroCliente = ["solingGSga", "solegrGSga"].includes(tsolInEg)

  // ==========================================================================
  // PASO 2: EXTRACCIÓN DEFENSIVA DE ARTÍCULOS
  // ==========================================================================
  const { data: rawArticulos, isLoading: isLoadingArticulos } = useQuery({
    queryKey: ["listaArticulosDinamica", tsolInEg, clicodigo],
    queryFn: async () => {
      try {
        if (filtroCliente && !clicodigo) return []
        const payload = { filtro_cliente: filtroCliente, clicodigo: clicodigo || "" }
        const response = await api.post("/CrearSolicitudIngEgr/getArticulosFiltroDinamico", payload)
        const rawData = response?.data?.data || response?.data || response || []
        return Array.isArray(rawData) ? rawData : Array.isArray(rawData?.data) ? rawData.data : []
      } catch (error) {
        return []
      }
    },
    enabled: !!tsolInEg,
  })

  const listaArticulos = rawArticulos || []

  // ==========================================================================
  // MANEJADORES DE LA GRILLA
  // ==========================================================================
  const handleAddRow = () => {
    setFormData((prev) => ({
      ...prev,
      detalle: [
        ...prev.detalle,
        {
          idCompuesto: "",
          invcodigo: "",
          artcodigo: "",
          sgacansol: 0,
          stock: 0,
          imagenes: [],
          documentos_pdf: [],
        },
      ],
    }))
  }

  const handleRemoveRow = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      detalle: prev.detalle.filter((_, index) => index !== indexToRemove),
    }))
  }

  // ==========================================================================
  // PASO 2: EXTRACCIÓN ULTRA ROBUSTA PARA MULTIMEDIA Y STOCK
  // ==========================================================================
  const fetchMultimediaYStock = async (invcodigo, artcodigo, index) => {
    setLoadingRow(index)
    try {
      const payload = { invcodigo, artcodigo, loccodigo: loccodigo || "", bodcodigo: bodcodigo || "" }
      const res = await api.post("/CrearSolicitudIngEgr/getMultimediaYStockArticulo", payload)

      // Lógica defensiva para buscar el núcleo de la data sin importar cuantas capas existan
      let details = { stock: 0, imagenes: [], documentos_pdf: [] }
      const r1 = res?.data

      // Validamos nivel por nivel para encontrar las listas "imagenes" o "documentos_pdf"
      if (r1?.data?.data?.imagenes || r1?.data?.data?.documentos_pdf || r1?.data?.data?.stock !== undefined) {
        details = r1.data.data
      } else if (r1?.data?.imagenes || r1?.data?.documentos_pdf || r1?.data?.stock !== undefined) {
        details = r1.data
      } else if (r1?.imagenes || r1?.documentos_pdf || r1?.stock !== undefined) {
        details = r1
      }

      setFormData((prev) => {
        const nuevoDetalle = [...prev.detalle]
        nuevoDetalle[index] = {
          ...nuevoDetalle[index],
          stock: details.stock || 0,
          // Garantizamos que si viene null/undefined se asigne un arreglo puro []
          imagenes: Array.isArray(details.imagenes) ? details.imagenes : [],
          documentos_pdf: Array.isArray(details.documentos_pdf) ? details.documentos_pdf : [],
        }
        return { ...prev, detalle: nuevoDetalle }
      })
    } catch (error) {
      console.error("Error obteniendo detalles multimedia:", error)
    } finally {
      setLoadingRow(null)
    }
  }

  const handleArticuloChange = (index, newValue) => {
    // PASO 4: Limpieza si el usuario borra el combo
    if (!newValue) {
      setFormData((prev) => {
        const nuevoDetalle = [...prev.detalle]
        nuevoDetalle[index] = {
          ...nuevoDetalle[index],
          idCompuesto: "",
          invcodigo: "",
          artcodigo: "",
          stock: 0,
          imagenes: [],
          documentos_pdf: [],
        }
        return { ...prev, detalle: nuevoDetalle }
      })
      return
    }

    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[index] = {
        ...nuevoDetalle[index],
        idCompuesto: String(newValue.id),
        invcodigo: newValue.invcodigo,
        artcodigo: newValue.artcodigo,
      }
      return { ...prev, detalle: nuevoDetalle }
    })

    // Disparamos la carga de multimedia y stock
    fetchMultimediaYStock(newValue.invcodigo, newValue.artcodigo, index)
  }

  const handleCantidadChange = (index, value) => {
    const val = parseFloat(value) || 0
    setFormData((prev) => {
      const nuevoDetalle = [...prev.detalle]
      nuevoDetalle[index] = { ...nuevoDetalle[index], sgacansol: val >= 0 ? val : 0 }
      return { ...prev, detalle: nuevoDetalle }
    })
  }

  // ==========================================================================
  // MANEJADOR DE MODALES DE VISUALIZACIÓN
  // ==========================================================================
  const openViewer = (type, title, items) => {
    setMediaViewer({ open: true, type, title, items })
  }

  const closeViewer = () => {
    setMediaViewer({ open: false, type: "", title: "", items: [] })
  }

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddCircleIcon />}
          onClick={handleAddRow}
          disabled={filtroCliente && !clicodigo}
        >
          Agregar Artículo
        </Button>
      </Box>

      {filtroCliente && !clicodigo && (
        <Box color="error.main" mb={2} fontSize="0.875rem">
          * Debe seleccionar un cliente primero para cargar el catálogo.
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#f5f7fa" }}>
            <TableRow>
              <TableCell width="5%">#</TableCell>
              <TableCell width="45%">Artículo (Código y Descripción)</TableCell>
              <TableCell width="15%" align="center">
                Stock Disp.
              </TableCell>
              <TableCell width="15%" align="center">
                Cant. Sol.
              </TableCell>
              <TableCell width="20%" align="center">
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detalle.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No hay artículos en el detalle.
                </TableCell>
              </TableRow>
            ) : (
              detalle.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>

                  <TableCell>
                    {/* PASO 3: CONFIGURACIÓN ESTRICTA DEL COMPONENTE */}
                    <Autocomplete
                      options={listaArticulos}
                      getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                      value={
                        listaArticulos.find((a) => String(a.id) === String(row.idCompuesto)) ||
                        (row.idCompuesto ? { id: row.idCompuesto, label: "Cargando..." } : null)
                      }
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                      onChange={(event, newValue) => handleArticuloChange(index, newValue)}
                      loading={isLoadingArticulos}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Buscar por código o descripción..."
                          variant="standard"
                          InputProps={{ ...params.InputProps, disableUnderline: true }}
                        />
                      )}
                    />
                  </TableCell>

                  <TableCell align="center">
                    {loadingRow === index ? <CircularProgress size={20} /> : <b>{row.stock || 0}</b>}
                  </TableCell>

                  <TableCell align="center">
                    <TextField
                      type="number"
                      variant="outlined"
                      size="small"
                      inputProps={{ min: 0, step: "0.01" }}
                      value={row.sgacansol === 0 ? "" : row.sgacansol}
                      onChange={(e) => handleCantidadChange(index, e.target.value)}
                      fullWidth
                    />
                  </TableCell>

                  <TableCell align="center">
                    {/* Botón Visor de Imágenes */}
                    <Tooltip title={row.imagenes?.length > 0 ? "Ver Imágenes" : "Sin Imágenes"}>
                      <span>
                        <IconButton
                          color="primary"
                          disabled={!row.imagenes || row.imagenes.length === 0}
                          onClick={() => openViewer("IMG", "Imágenes del Producto", row.imagenes)}
                        >
                          <ImageIcon />
                        </IconButton>
                      </span>
                    </Tooltip>

                    {/* Botón Visor de PDFs */}
                    <Tooltip title={row.documentos_pdf?.length > 0 ? "Ver PDFs" : "Sin Documentos PDF"}>
                      <span>
                        <IconButton
                          color="secondary"
                          disabled={!row.documentos_pdf || row.documentos_pdf.length === 0}
                          onClick={() => openViewer("PDF", "Documentos Técnicos PDF", row.documentos_pdf)}
                        >
                          <PictureAsPdfIcon />
                        </IconButton>
                      </span>
                    </Tooltip>

                    {/* Botón Eliminar Fila */}
                    <Tooltip title="Eliminar Fila">
                      <IconButton color="error" onClick={() => handleRemoveRow(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* =========================================================
          MODAL VISUALIZADOR DE MULTIMEDIA (IMÁGENES Y PDF)
      ========================================================= */}
      <Dialog open={mediaViewer.open} onClose={closeViewer} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          {mediaViewer.title}
          <IconButton color="inherit" onClick={closeViewer}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, backgroundColor: "#f4f6f8" }}>
          <Box display="flex" flexDirection="column" gap={3} alignItems="center">
            {mediaViewer.items.map((item, i) => (
              <Paper
                key={i}
                elevation={3}
                sx={{ p: 2, width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}
              >
                {mediaViewer.type === "IMG" ? (
                  <img
                    src={item.base64}
                    alt={`Imagen-${item.id}`}
                    style={{ maxWidth: "100%", maxHeight: "600px", objectFit: "contain" }}
                  />
                ) : (
                  <>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                      {item.nombre}
                    </Typography>
                    <iframe
                      src={item.base64}
                      title={`PDF-${item.id}`}
                      width="100%"
                      height="600px"
                      style={{ border: "none" }}
                    />
                  </>
                )}
              </Paper>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default DetalleArticulosGrid
